<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import "@jobinsjp/vue3-datatable/dist/style.css";
import {
  DataTable,
  TableBody,
  TableHead,
  TableBodyCell,
} from "@jobinsjp/vue3-datatable";
import moment from "moment";
import { truncate } from "lodash";
import Swal from "sweetalert2";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import {
  announcementCategories,
  announcementCategoryLabel,
} from "@/helpers/constants";

const store = useStore();
const loading = computed(() => store.state.globals.loading);
const announcements = computed(() => store.state.announcement.announcement);
const pagination = computed(() => store.state.announcement.pagination);

const userRoles = computed(() => JSON.parse(localStorage.getItem("roles")));
const roleExist = (slug) => {
  if (userRoles.value && userRoles.value.length) {
    return userRoles.value.some((obj) => obj.slug === slug);
  }

  return false;
};

const title = ref("Announcement - List");
const items = ref([
  {
    text: "Home",
    href: "/",
  },
  {
    text: "Announcement",
    href: "/announcement",
  },
  {
    text: "List",
    active: true,
  },
]);

const query = ref({
  search: "",
});

const categoryFilter = ref("");
const lastLoad = ref({ page: 1, per_page: 10, search: "" });

const getAnnouncements = async (dt) => {
  lastLoad.value = dt;

  await store.dispatch("announcement/getAnnouncements", {
    page: dt.page,
    pageSize: dt.per_page,
    sortColumn: "updated_at",
    search: dt.search,
    orderBy: "desc",
    category: categoryFilter.value || undefined,
  });
};

const onCategoryFilterChange = () => {
  getAnnouncements({ ...lastLoad.value, page: 1 });
};

const formattedDate = (date) => {
  return moment(date).format("YYYY-MM-DD h:mm A");
};

const duration = (row) => {
  if (!row.starts_at && !row.ends_at) return "No duration set";

  const from = row.starts_at ? formattedDate(row.starts_at) : "Immediately";
  const to = row.ends_at ? formattedDate(row.ends_at) : "No end";

  return `${from} → ${to}`;
};

const cameraSummary = (row) => {
  if (row.category !== "camera") return "All (Organization)";
  if (!row.cameras || !row.cameras.length) return "No camera assigned";

  return row.cameras.map((camera) => camera.camera_id).join(", ");
};

const archiveAnnouncement = (id, title) => {
  Swal.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: "Archive",
  }).then((result) => {
    if (result.value) {
      store.dispatch("announcement/deleteAnnouncement", id);
      Swal.fire(
        "Archive!",
        `Announcement ${title} has been successfully archive!`,
        "success"
      );
    }
  });
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
      <div class="d-flex mb-4 justify-content-between">
        <div class="d-flex align-items-center gap-3">
          <h1 class="fs-4 text mb-0">Announcement</h1>
          <select
            class="form-select form-select-sm w-auto"
            v-model="categoryFilter"
            @change="onCategoryFilterChange"
            title="Filter by category"
          >
            <option value="">All Categories</option>
            <option
              v-for="option in announcementCategories"
              :key="option.value"
              :value="option.value"
            >
              {{ option.name }}
            </option>
          </select>
        </div>
        <div>
          <router-link
            v-if="roleExist('add-announcement')"
            to="/announcement/create"
            class="btn btn-primary right"
          >
            <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
            New Announcement
          </router-link>
        </div>
      </div>

      <div class="row">
        <div class="col lg-12">
          <data-table
            :rows="announcements"
            :pagination="pagination"
            striped
            :loading="loading"
            :query="query"
            filter
            hoverable
            sortable
            @loadData="getAnnouncements"
          >
            <template #thead>
              <table-head>Title</table-head>
              <table-head>Description</table-head>
              <table-head>Category</table-head>
              <table-head>Camera(s)</table-head>
              <table-head>Duration</table-head>
              <table-head>Status</table-head>
              <table-head>Date Modified</table-head>
              <table-head>Actions</table-head>
            </template>

            <template #tbody="{ row }">
              <table-body v-text="row.title" :title="row.title" />
              <table-body
                v-text="truncate(row.description, { length: 50 })"
                :description="row.description"
              />
              <table-body>
                <b-badge
                  :variant="row.category == 'camera' ? 'info' : 'secondary'"
                >
                  {{ announcementCategoryLabel(row.category) }}
                </b-badge>
              </table-body>
              <table-body>
                <span :title="cameraSummary(row)">
                  {{ truncate(cameraSummary(row), { length: 40 }) }}
                </span>
              </table-body>
              <table-body v-text="duration(row)" />
              <table-body>
                <b-badge :variant="row.status == 0 ? 'success' : 'danger'">
                  {{ row.status == 0 ? "Active" : "Archive" }}
                </b-badge>
                <b-badge
                  v-if="row.auto_archived_at"
                  variant="warning"
                  class="ms-1"
                  :title="`Auto-archived on ${formattedDate(row.auto_archived_at)}`"
                >
                  Expired
                </b-badge>
              </table-body>
              <table-body v-text="formattedDate(row.updated_at)" />
              <table-body>
                <router-link
                  v-if="roleExist('edit-announcement')"
                  :to="`/announcement/${row.id}/edit`"
                  class="btn btn-info mr-3"
                  title="Edit"
                >
                  <i class="ri-pencil-line label-icon align-middle"></i>
                </router-link>
                <button
                  v-if="row.status == 0 && roleExist('delete-announcement')"
                  class="btn btn-danger"
                  type="button"
                  title="Archive"
                  @click.prevent="archiveAnnouncement(row.id, row.title)"
                >
                  <i class="mdi mdi-archive-alert label-icon align-middle"></i>
                </button>
              </table-body>
            </template>

            <template #empty>
              <TableBodyCell colspan="10">
                No Announcement found.
              </TableBodyCell>
            </template>
          </data-table>
        </div>
      </div>
    </b-overlay>
  </Layout>
</template>
